import {
  Create,
  DateField,
  Edit,
  List,
  Show,
  SimpleShowLayout,
  TextField,
  TextInput,
  Toolbar,
  TopToolbar,
  required,
  SimpleForm,
  useRecordContext,
  useNotify,
  useRedirect,
  SaveButton,
  DeleteButton,
  EditButton,
  CreateButton
} from 'react-admin';
import { Box, Button } from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import { ACCOUNT_STATUS, AUTHORITY_CHOICES } from '../common_modules/constants/choices';
import EnumTextField from '../common_modules/components/EnumTextField';
import NumericRadioButtonGroupInput from '../common_modules/components/NumericRadioButtonGroupInput';
import useErrorHandler from '../common_modules/hooks/useErrorHandler';
import FlexibleListLayout from '../common_modules/components/FlexibleListLayout';
import FormValidation from '../common_modules/components/FormValidation';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
// インターフェース定義
interface Staff {
  name: string;
  email: string;
  authority: string;
}

const ACCOUNT_STATUS_EDIT = ACCOUNT_STATUS.filter((status) => status.id !== 2);
// スタッフのリストアクション用カスタムボタン
const CustomCreateButton = () => <CreateButton label="スタッフ追加" icon={<PersonAddAlt1Icon />} />;

const ListActions = () => (
  <TopToolbar>
    <CustomCreateButton />
  </TopToolbar>
);

// カスタム削除ボタン
const CustomDeleteButton = () => {
  const record = useRecordContext();
  return (
    <Box sx={{ marginLeft: '16px' }}>
      <DeleteButton confirmTitle={`${record?.name} を削除`} confirmContent="本当に削除しますか？" />
    </Box>
  );
};

// 保存ボタンと削除ボタンを含むツールバー
const CustomEditToolbar = ({ isFormValid }: { isFormValid: boolean }) => (
  <Toolbar sx={{ justifyContent: 'space-between', width: '100%', mt: 2 }}>
    <SaveButton disabled={!isFormValid} />
    <CustomDeleteButton />
  </Toolbar>
);

// カスタムの作成用ツールバー
const CustomCreateToolbar = ({ isFormValid }: { isFormValid: boolean }) => (
  <Toolbar sx={{ justifyContent: 'space-between', width: '100%', mt: 2 }}>
    <SaveButton label="登録" icon={<MailOutlineIcon />} disabled={!isFormValid} />
  </Toolbar>
);

// スタッフリストの表示
export const StaffList = () => (
  <List
    exporter={false}
    actions={<ListActions />}
    sx={{
      '& .MuiPaper-root': {
        boxShadow: {
          xs: 'none',
          sm: 'none',
          md: '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)'
        }
      },
      '& .RaList-main': {
        marginTop: { sm: '8px', md: 0 }
      },
      '& .RaList-actions': {
        height: { xs: 0, sm: 0, md: '48px' }
      }
    }}
  >
    <FlexibleListLayout rowClick="show" bulkActionButtons={false}>
      <TextField source="name" />
      <TextField source="email" />
      <EnumTextField source="authority" translationKeyPrefix="values.authority" />
      <EnumTextField source="accountInvalidFlag" translationKeyPrefix="values.accountInvalidFlag" />
      <DateField showTime={true} source="lastLoginDate" />
      {/* <EditButton /> */}
      <ButtonWithIconAndText text="編集" icon={<EditIcon />} alignment="left" routeOption="edit" />
    </FlexibleListLayout>
  </List>
);

interface ConditionalFieldProps {
  source: string;
  choices: Array<{ id: number; name: string }>;
  validate?: any;
}
const ConditionalNumericRadioButtonGroupInput = ({ source, choices, validate }: ConditionalFieldProps) => {
  const record = useRecordContext();
  return (
    <NumericRadioButtonGroupInput
      source={source}
      choices={choices}
      disabled={record?.accountInvalidFlag === 2}
      validate={validate}
      helperText={false}
    />
  );
};
// StaffEdit コンポーネント
export const StaffEdit = () => {
  const handleError = useErrorHandler();
  const queryClient = useQueryClient();
  const notify = useNotify();
  const requiredFields = ['name', 'authority', 'accountInvalidFlag'];

  return (
    <Edit
      actions={<ButtonWithIconAndText text="詳細" icon={<VisibilityIcon />} alignment="right" routeOption="show" />}
      resource="accounts"
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          notify('保存しました。', { type: 'info' });
          queryClient.invalidateQueries({ queryKey: ['auth', 'getIdentity'] });
        },
        onError: handleError
      }}
    >
      <SimpleForm
        toolbar={
          <FormValidation requiredFields={requiredFields}>
            {(isFormValid) => <CustomEditToolbar isFormValid={isFormValid} />}
          </FormValidation>
        }
        sx={{
          maxWidth: '600px',
          pb: { xs: '16px !important' }
        }}
      >
        <TextInput source="name" validate={required()} />
        <SimpleShowLayout sx={{ paddingLeft: '0' }}>
          <TextField source="email" />
        </SimpleShowLayout>
        <NumericRadioButtonGroupInput
          source="authority"
          choices={AUTHORITY_CHOICES}
          validate={required()}
          helperText={false}
        />
        <ConditionalNumericRadioButtonGroupInput
          source="accountInvalidFlag"
          choices={ACCOUNT_STATUS_EDIT}
          validate={required()}
        />
      </SimpleForm>
    </Edit>
  );
};

// StaffCreate コンポーネント
export const StaffCreate = () => {
  const handleError = useErrorHandler();
  const notify = useNotify();
  const redirect = useRedirect();

  // 必須フィールドの定義
  const requiredFields = ['name', 'email', 'authority'];

  return (
    <Create
      title="スタッフ追加"
      mutationOptions={{
        onSuccess: (data: Staff) => {
          notify(`${data.name}を登録しました`, { type: 'info' });
          redirect('list', 'accounts');
        },
        onError: handleError
      }}
    >
      <SimpleForm
        toolbar={
          <FormValidation requiredFields={requiredFields}>
            {(isFormValid) => <CustomCreateToolbar isFormValid={isFormValid} />}
          </FormValidation>
        }
        sx={{ maxWidth: '600px' }}
      >
        <TextInput source="name" fullWidth validate={required()} />
        <TextInput source="email" fullWidth validate={required()} />
        <NumericRadioButtonGroupInput source="authority" choices={AUTHORITY_CHOICES} validate={required()} />
      </SimpleForm>
    </Create>
  );
};

// StaffShow コンポーネント
export const StaffShow = () => (
  <Show
    title="スタッフ管理 - 詳細"
    actions={<ButtonWithIconAndText text="編集" icon={<EditIcon />} alignment="right" routeOption="edit" />}
  >
    <SimpleShowLayout>
      <TextField source="name" />
      <TextField source="email" />
      <EnumTextField source="authority" translationKeyPrefix="values.authority" />
      <EnumTextField source="accountInvalidFlag" translationKeyPrefix="values.accountInvalidFlag" />
      <DateField showTime={true} source="lastLoginDate" />
    </SimpleShowLayout>
  </Show>
);

interface ButtonWithIconAndTextProps {
  text: string;
  icon: JSX.Element;
  alignment: 'left' | 'right';
  routeOption: 'edit' | 'show';
  basePath?: string;
}

export const ButtonWithIconAndText = ({
  text,
  icon,
  alignment,
  routeOption,
  basePath = 'accounts'
}: ButtonWithIconAndTextProps) => {
  const record = useRecordContext();
  const navigate = useNavigate();

  if (!record) return null;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/${basePath}/${record.id}/${routeOption}`);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: alignment === 'left' ? 'flex-start' : 'flex-end',
        height: { xs: '48px', sm: '48px', md: '30.75px' }
      }}
    >
      <Button onClick={handleClick} size="small" startIcon={icon}>
        {text}
      </Button>
    </Box>
  );
};
